import React, { useEffect, useMemo, useRef, useState } from "react";

export type SearchOption = {
  value: string;
  label: string;
  searchText: string;
};

type Props = {
  label: string;
  value: string;
  options: SearchOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
};

function compact(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function matchesSearch(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const h = haystack.toLowerCase();
  const words = q.split(/\s+/).filter(Boolean);
  if (words.every((word) => h.includes(word))) return true;
  const compactH = compact(h);
  const compactQ = compact(q);
  if (compactQ && compactH.includes(compactQ)) return true;
  let i = 0;
  for (const ch of compactH) {
    if (ch === compactQ[i]) i += 1;
    if (i >= compactQ.length) return true;
  }
  return false;
}

const EventSearchSelect: React.FC<Props> = ({
  label,
  value,
  options,
  onChange,
  placeholder = "Type to search…",
  disabled,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((opt) => opt.value === value) ?? null;

  useEffect(() => {
    if (!open) setQuery("");
  }, [open, value]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    return options.filter((opt) => matchesSearch(`${opt.label} ${opt.searchText}`, query)).slice(0, 80);
  }, [options, query]);

  return (
    <div className="event-search-select" ref={rootRef}>
      <span className="event-search-select__label">{label}</span>
      <input
        type="search"
        className="event-search-select__input"
        value={open ? query : selected?.label ?? ""}
        placeholder={loading ? "Loading…" : placeholder}
        disabled={disabled || loading}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange("");
        }}
        autoComplete="off"
      />
      {open && !disabled ? (
        <ul className="event-search-select__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="event-search-select__empty">No matching events</li>
          ) : (
            filtered.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  className={
                    opt.value === value
                      ? "event-search-select__option is-selected"
                      : "event-search-select__option"
                  }
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default EventSearchSelect;
